<template>
    <div class="wellness-dots">
        <span v-if="wellnessWithColors.length === 0" class="no-data">{{ $t('common.no_data_yet') }}</span>
        <v-tooltip v-for="(item, i) in wellnessWithColors" :key="i" :top="!openBelow" :bottom="openBelow"
            :color="tooltipBg" content-class="bp-tooltip" :value="hoveredIndex === i">
            <template v-slot:activator="{ attrs }">
                <div v-bind="attrs" class="w-dot" :class="{ 'w-dot--marked': isMarked(item, i) }"
                    :style="{ backgroundColor: item.bg, color: item.color }"
                    @pointerenter="onDotEnter(item, i, $event)"
                    @pointerleave="onDotLeave($event)"
                    @click="onDotClick(item, i, $event)">
                    {{ getDayInitial(item.date) }}
                </div>
            </template>
            <div class="tooltip-content">
                <div class="tt-date">{{ item.date }}</div>
                <div class="tt-type" :style="{ color: item.color }">{{ item.status }}</div>
                <div class="tt-row"><span>{{ $t('common.energy') }}</span><span>{{ item.energy }}</span></div>
                <div class="tt-row"><span>{{ $t('common.muscles_hurt') }}</span><span>{{ item.muscles_hurt }}</span></div>
                <div class="tt-row"><span>{{ $t('common.sleep_night') }}</span><span>{{ item.sleep_night }}</span></div>
                <div class="tt-row"><span>{{ $t('common.stressed') }}</span><span>{{ item.stressed }}</span></div>
                <div class="tt-row"><span>{{ $t('common.tired') }}</span><span>{{ item.tired }}</span></div>
                <hr class="tt-divider">
                <div class="tt-sum"><span>{{ $t('common.sum') }}</span><span>{{ item.sum }}</span></div>
            </div>
        </v-tooltip>
    </div>
</template>

<script>
import { filterValidWellness, getWellnessScore, getWellnessStatusKey } from '@/utils/Routine'

const TOOLTIP_CLEARANCE = 210

const WELLNESS_PALETTE = {
    veryGood: { color: '#4ADE80', bg: 'rgba(74,222,128,0.22)' },
    good: { color: '#34D399', bg: 'rgba(52,211,153,0.22)' },
    somewhatGood: { color: '#22D3EE', bg: 'rgba(34,211,238,0.22)' },
    normal: { color: '#94A3B8', bg: 'rgba(148,163,184,0.22)' },
    somewhatBad: { color: '#FBBF24', bg: 'rgba(251,191,36,0.28)' },
    bad: { color: '#FB923C', bg: 'rgba(251,146,60,0.30)' },
    veryBad: { color: '#F87171', bg: 'rgba(239,68,68,0.30)' },
}

export default {
    name: "WellnessDots",
    props: {
        wellness: {
            type: Array,
            default: () => [],
        },
        highlightDate: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            hoveredIndex: null,
            pointerType: 'mouse',
            openBelow: false,
        }
    },
    computed: {
        wellnessWithColors(vm = this) {
            const items = filterValidWellness(vm.wellness)
            const result = []
            for (let i = 5; i < items.length; i++) {
                const score = getWellnessScore(items, i)
                const statusKey = getWellnessStatusKey(score)
                const palette = WELLNESS_PALETTE[statusKey] || WELLNESS_PALETTE.normal
                result.push({
                    ...items[i],
                    color: palette.color,
                    bg: palette.bg,
                    status: vm.$t(`common.wellnessStatus.${statusKey}`),
                })
            }
            return result.slice(-7)
        },
    },
    methods: {
        getDayInitial(date) {
            const vm = this
            const m = vm.$moment(date, 'DD-MM-YYYY')
            if (vm.$i18n.locale !== 'en' && m.day() === 3) return 'X'
            return m.format('dd').charAt(0).toUpperCase()
        },
        isMarked(item, index) {
            const vm = this
            return vm.hoveredIndex !== index && !!vm.highlightDate && item.date === vm.highlightDate
        },
        safeAreaTop() {
            const value = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top')
            return parseFloat(value) || 0
        },
        openDot(item, index, el) {
            const vm = this
            if (el && el.getBoundingClientRect) {
                const top = el.getBoundingClientRect().top
                vm.openBelow = top - vm.safeAreaTop() < TOOLTIP_CLEARANCE
            }
            vm.hoveredIndex = index
            vm.$emit('hover', item.date || null)
        },
        closeDot() {
            const vm = this
            vm.hoveredIndex = null
            vm.$emit('hover', null)
            vm.unbindOutsideClose()
        },
        onDotEnter(item, index, event) {
            const vm = this
            vm.pointerType = (event && event.pointerType) || 'mouse'
            if (vm.pointerType !== 'mouse') return
            vm.openDot(item, index, event && event.currentTarget)
        },
        onDotLeave(event) {
            const vm = this
            if (event && event.pointerType && event.pointerType !== 'mouse') return
            vm.closeDot()
        },
        onDotClick(item, index, event) {
            const vm = this
            const el = event && event.currentTarget
            if (el && el.blur) el.blur()
            if (vm.$listeners['click:dot']) {
                vm.closeDot()
                return vm.$emit('click:dot', item)
            }
            if (vm.pointerType === 'mouse') return
            if (vm.hoveredIndex === index) return vm.closeDot()
            vm.openDot(item, index, el)
            vm.bindOutsideClose()
        },
        bindOutsideClose() {
            const vm = this
            if (vm._outsideClose) return
            vm._outsideClose = (event) => {
                if (event.target && event.target.closest && event.target.closest('.w-dot')) return
                vm.closeDot()
            }
            document.addEventListener('click', vm._outsideClose, true)
        },
        unbindOutsideClose() {
            const vm = this
            if (!vm._outsideClose) return
            document.removeEventListener('click', vm._outsideClose, true)
            vm._outsideClose = null
        },
    },
    beforeDestroy() {
        this.unbindOutsideClose()
    },
}
</script>

<style scoped>
.wellness-dots {
    display: inline-flex;
    gap: 3px;
    align-items: center;
}

.w-dot {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    font-weight: 800;
    font-size: 10px;
    letter-spacing: 0.03em;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.12s ease;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
}

.w-dot:hover {
    transform: scale(1.15);
}

.w-dot--marked {
    box-shadow: 0 0 0 2px currentColor;
}

.tooltip-content {
    text-align: left;
    min-width: 150px;
}

.tt-date {
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 5px;
    text-align: center;
}

.tt-type {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    text-align: center;
}

.tt-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    line-height: 1.7;
}

.tt-row span:last-child {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.tt-divider {
    border: none;
    border-top: 1px solid var(--v-outline-base);
    margin: 6px 0;
}

.tt-sum {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.no-data {
    color: var(--v-txtMuted-base);
    font-size: 12px;
    font-style: italic;
}
</style>
